// apps/ers/src/app/modules/ers-leave-management/dialogs/hourly-permission-dialog/hourly-permission-dialog.component.ts
// PBI 19505 – Hourly Permission

import {
  Component,
  Inject,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewChild,
  DestroyRef,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  FormControl,
  Validators,
  ReactiveFormsModule,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import {
  MatDialogModule,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  MatNativeDateModule,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ApiNotificationService } from '@rcs/notify';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import {
  CreateHourlyRequestDto,
  LeaveType,
  Supervisor,
} from '../../models/leave.model';
import { LeaveService } from '../../services/leave.service';
import { toISODate } from '../../utils/date.utils';
import { TranslocoPipe } from '@jsverse/transloco';
import { ErsSearchableSelectComponent } from '@rcs/ers-elementals';
import { ErsSortByPipe } from '@rcs/ers-elementals';
import { filterAndSortOptions } from '@rcs/utils';

// ── MM/DD/YYYY date display ───────────────────────────────────────────────────
const LM_DATE_FORMATS = {
  parse: { dateInput: 'MM/DD/YYYY' },
  display: {
    dateInput: 'MM/DD/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

// ── Cross-field validator: endTime > startTime ────────────────────────────────
function endTimeAfterStart(g: AbstractControl): ValidationErrors | null {
  const start = g.get('fromTime')?.value as string;
  const end = g.get('toTime')?.value as string;
  if (start && end && end <= start) {
    return { endTimeNotAfterStart: true };
  }
  return null;
}

export const HOURLY_REASONS: { value: string; label: string }[] = [
  { value: 'doctor_appointment', label: 'Doctor appointment' },
  { value: 'family_emergency', label: 'Family emergency' },
  { value: 'power_outage', label: 'Temporary internet / power outage at home' },
  { value: 'house_maintenance', label: 'House maintenance or repair work' },
  { value: 'personal_work', label: 'Personal work' },
  { value: 'bank_or_government_errand', label: 'Bank / government errand' },
  { value: 'other', label: 'Other' },
];

@Component({
  selector: 'ers-hourly-permission-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: MAT_DATE_FORMATS, useValue: LM_DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'en-US' },
  ],
  imports: [
    TranslocoPipe,
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    ErsSearchableSelectComponent,
    ErsSortByPipe,
  ],
  templateUrl: './hourly-permission-dialog.component.html',
  styleUrls: ['./hourly-permission-dialog.component.scss'],
})
export class HourlyPermissionDialogComponent implements OnInit {
  // ── Reference data ────────────────────────────────────────────────────────
  supervisors: Supervisor[] = [];
  isLoadingSupervisors = false;
  leaveTypes: LeaveType[] = [];
  isLoadingLeaveTypes = false;
  readonly reasons = HOURLY_REASONS;
  readonly today = new Date();

  // ── Leave-type autocomplete ───────────────────────────────────────────────
  @ViewChild('leaveTypeInput', { read: MatAutocompleteTrigger })
  leaveTypeAutoTrigger!: MatAutocompleteTrigger;

  leaveTypeSearchCtrl = new FormControl('');

  get filteredLeaveTypes(): LeaveType[] {
    return filterAndSortOptions(
      this.leaveTypes,
      this.leaveTypeSearchCtrl.value ?? '',
      (t) => t.name
    );
  }

  // ── Selected supervisor chips ─────────────────────────────────────────────
  selectedSupervisors: Supervisor[] = [];

  // ── Form ──────────────────────────────────────────────────────────────────
  hourlyForm!: FormGroup;

  // ── State ─────────────────────────────────────────────────────────────────
  isSubmitting = false;

  get showOtherReason(): boolean {
    return this.hourlyForm.get('reasonCode')?.value === 'other';
  }

  get isSingleSupervisor(): boolean {
    return this.supervisors.length === 1;
  }

  private destroyRef = inject(DestroyRef);
  private notify = inject(ApiNotificationService);

  constructor(
    private fb: FormBuilder,
    private leaveService: LeaveService,
    private dialogRef: MatDialogRef<HourlyPermissionDialogComponent>,
    private cdr: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.hourlyForm = this.fb.group(
      {
        requestDate: [new Date(), Validators.required],
        leaveTypeId: [null, Validators.required],
        fromTime: ['09:00', Validators.required],
        toTime: ['10:00', Validators.required],
        reasonCode: [null, Validators.required],
        pleaseSpecify: [''],
      },
      { validators: endTimeAfterStart }
    );

    // "Please Specify" is mandatory when reasonCode === 'other'
    this.hourlyForm.get('reasonCode')!.valueChanges.subscribe((v) => {
      const specCtrl = this.hourlyForm.get('pleaseSpecify')!;
      if (v === 'other') {
        specCtrl.setValidators([
          Validators.required,
          Validators.maxLength(500),
        ]);
      } else {
        specCtrl.clearValidators();
      }
      specCtrl.updateValueAndValidity();
    });
  }

  ngOnInit(): void {
    this.loadSupervisors();
    this.loadLeaveTypes();
  }

  // ── Data loading ──────────────────────────────────────────────────────────

  private loadSupervisors(): void {
    this.isLoadingSupervisors = true;
    this.leaveService
      .getSupervisors()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (list) => {
          this.supervisors = list ?? [];
          this.isLoadingSupervisors = false;
          // Auto-select single supervisor
          if (this.supervisors.length === 1) {
            this.selectedSupervisors = [...this.supervisors];
          }
          this.cdr.markForCheck();
        },
        error: () => {
          this.isLoadingSupervisors = false;
          this.cdr.markForCheck();
        },
      });
  }

  private loadLeaveTypes(): void {
    this.isLoadingLeaveTypes = true;
    this.leaveService
      .getLeaveTypes()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (types) => {
          this.leaveTypes = (types ?? []).filter((t) => t.isActive);
          this.isLoadingLeaveTypes = false;
          // Auto-select when there is exactly one leave type
          if (this.leaveTypes.length === 1) {
            this.hourlyForm.patchValue({ leaveTypeId: this.leaveTypes[0].id });
            this.leaveTypeSearchCtrl.setValue(this.leaveTypes[0].name, {
              emitEvent: false,
            });
          }
          this.cdr.markForCheck();
        },
        error: () => {
          this.isLoadingLeaveTypes = false;
          this.cdr.markForCheck();
        },
      });
  }

  onLeaveTypeSelected(event: MatAutocompleteSelectedEvent): void {
    const id = event.option.value as string;
    const found = this.leaveTypes.find((t) => t.id === id);
    if (found) {
      this.hourlyForm.patchValue({ leaveTypeId: id });
      this.leaveTypeSearchCtrl.setValue(found.name, { emitEvent: false });
    }
  }

  toggleLeaveTypePanel(event: Event): void {
    event.stopPropagation();
    if (this.leaveTypeAutoTrigger?.panelOpen) {
      this.leaveTypeAutoTrigger.closePanel();
    } else {
      this.leaveTypeAutoTrigger?.openPanel();
    }
  }

  // ── Supervisor chips ──────────────────────────────────────────────────────

  addSupervisor(sup: Supervisor): void {
    if (
      !this.selectedSupervisors.find(
        (s) => s.supervisorEmployeeId === sup.supervisorEmployeeId
      )
    ) {
      this.selectedSupervisors = [...this.selectedSupervisors, sup];
    }
  }

  removeSupervisor(sup: Supervisor): void {
    this.selectedSupervisors = this.selectedSupervisors.filter(
      (s) => s.supervisorEmployeeId !== sup.supervisorEmployeeId
    );
  }

  // ── Date filter: no past dates ────────────────────────────────────────────
  futureDateFilter = (d: Date | null): boolean =>
    !!d && d >= new Date(this.today.toDateString());

  // ── Submit ────────────────────────────────────────────────────────────────

  onSubmit(): void {
    this.hourlyForm.markAllAsTouched();
    if (this.hourlyForm.invalid || this.selectedSupervisors.length === 0)
      return;

    this.isSubmitting = true;
    const v = this.hourlyForm.value;

    const reason =
      v.reasonCode === 'other'
        ? v.pleaseSpecify
        : this.reasons.find((r) => r.value === v.reasonCode)?.label ??
          v.reasonCode;

    const dto: CreateHourlyRequestDto = {
      requestDate: this.toIsoDate(v.requestDate),
      leaveTypeId: v.leaveTypeId,
      fromTime: v.fromTime,
      toTime: v.toTime,
      reason,
      requestedToEmployeeIds: this.selectedSupervisors.map(
        (s) => s.supervisorEmployeeId
      ),
    };

    this.leaveService.createHourlyRequest(dto).subscribe({
      next: (res) => {
        this.isSubmitting = false;
        if (!res?.success) {
          const msg =
            res?.error ?? 'Failed to submit request. Please try again.';
          this.notify.addError(msg);
          this.cdr.markForCheck();
          return;
        }
        this.dialogRef.close({ success: true, data: res });
      },
      error: () => {
        this.isSubmitting = false;
        this.notify.addError('An unexpected error occurred. Please try again.');
        this.cdr.markForCheck();
      },
    });
  }

  onCancel(): void {
    this.dialogRef.close({ success: false });
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  private toIsoDate(d: Date | string): string {
    return toISODate(d);
  }

  /** Convert 24h "HH:mm" → "HH:MM AM/PM" for display hint */
  format12h(time: string): string {
    if (!time) return '';
    const [h, m] = time.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${String(h12).padStart(2, '0')}:${String(m).padStart(
      2,
      '0'
    )} ${ampm}`;
  }
}
